import { MULTIPLE_COLORS } from "@roman/shared/constants/predictionChart";
import { useIsMobile } from "@roman/shared/hooks/soccer/useIsMobile";
import type { PredictionMarketChoiceWithProbability } from "@roman/shared/types/PredictionMarket";
import { cn } from "@roman/shared/utils";
import {
  PredictionMarketType,
  getPredictionMarketType,
  getYesNoChoices,
} from "@roman/shared/utils/predictionMarket/utils";
import { ChoiceCardGraph } from "./ChoiceCardGraph";

interface MultipleChoicesGraphProps {
  choices: PredictionMarketChoiceWithProbability[];
  outlined?: boolean;
  onlyProbability?: boolean;
  showAll?: boolean;
  selectedChoiceIds: string[];
}

export const MultipleChoicesGraph = ({ choices, showAll = false, selectedChoiceIds }: MultipleChoicesGraphProps) => {
  const predictionMarketType = getPredictionMarketType(choices.map((c) => ({ name: c.name })));

  const isMobile = useIsMobile();

  if (predictionMarketType === PredictionMarketType.TwoChoice) {
    const { yesChoice, noChoice } = getYesNoChoices(choices);
    const yesPercentage =
      yesChoice.probability * 100 >= 1
        ? Math.round(yesChoice.probability * 100) === 100
          ? "99"
          : Math.round(yesChoice.probability * 100)
        : "<1";
    const noPercentage =
      noChoice.probability * 100 >= 1
        ? Math.round(noChoice.probability * 100) === 100
          ? "99"
          : Math.round(noChoice.probability * 100)
        : "<1";

    // Always use horizontal flex, let text wrap naturally if needed
    return (
      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-2">
          {/* Yes Choice Row */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1">
              <span className="w-[10px] h-[10px] rounded-full bg-[#00AFCE] flex-shrink-0" />
              <span className="text-dark-gray font-roboto font-normal text-[14px] leading-none">{yesChoice.name}</span>
            </div>
            <span className="text-dark-gray font-roboto font-bold text-[16px] leading-[16px] w-[30px] text-right">
              {yesPercentage}%
            </span>
          </div>

          {/* No Choice Row */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1">
              <span className="w-[10px] h-[10px] rounded-full bg-[#FF7FD2] flex-shrink-0" />
              <span className="text-dark-gray font-roboto font-normal text-[14px] leading-none">{noChoice.name}</span>
            </div>
            <span className="text-dark-gray font-roboto font-bold text-[16px] leading-[16px] w-[30px] text-right">
              {noPercentage}%
            </span>
          </div>
        </div>
        <img src="/assets/branch_text.png" alt="Branch text" className={isMobile ? "w-[85px]" : "w-[100px]"} />
      </div>
    );
  }

  choices.sort((a, b) => b.probability - a.probability);
  const displayedChoices = choices
    .filter((choice) => selectedChoiceIds.includes(choice.id))
    .slice(0, 4)
    .map((choice) => choice.id);

  return (
    <div className="flex justify-between items-start">
      <div className="flex w-full justify-between items-start">
        <div className={cn("grid gap-1 overflow-y-scroll hide-scrollbar flex-3", showAll ? "h-full" : "h-[90px]")}>
          {choices.map((choice, index) => (
            <div className={cn(!displayedChoices.includes(choice.id) ? "hidden" : "")}>
              <ChoiceCardGraph
                key={choice.id}
                id={choice.id}
                name={choice.name}
                probability={choice.probability}
                color={MULTIPLE_COLORS[index % MULTIPLE_COLORS.length]}
                onClick={() => {}}
              />
            </div>
          ))}
        </div>
        <img src="/assets/branch_text.png" alt="Branch text" className={isMobile ? "w-[85px]" : "w-[100px]"} />
      </div>
    </div>
  );
};
